import { mapGetters, mapMutations } from 'vuex'
import sanitaizeHtml from './sanitaizeHtml'
import trustCenter from '@/mixins/trustCenter'
import questionnaireUploadSettings from '@/mixins/questionnaireUploadSettings'
import { appURL } from '~/env.js'

export default {
  mixins: [sanitaizeHtml, trustCenter, questionnaireUploadSettings],

  data() {
    return {
      BREAKPOINTS: {
        XS: 500,
        SM: 700,
        SM_PLUS: 730,
        MD: 1200,
        LG: 1280,
        LG_PLUS: 1300,
        XL: 1400,
      },
      NAVBAR_WIDTHS: {
        XS: 200,
        SM: 222,
        MD: 220,
        LG: 170,
        LG_PLUS: 180,
        XL: 200,
        DEFAULT: 244,
      },
    }
  },

  methods: {
    ...mapMutations(['SET_CURRENT_LINK', 'SET_CURRENT_TAB']),
    containsHTMLTags(inputString) {
      // block all html tags that user can add content via them like iframe/images etc...
      const allowedTagsRegex =
        /<(?!img|audio|video|iframe|object|embed|canvas|input|textarea|select|script)[a-z][\s\S]*>/i
      return allowedTagsRegex.test(inputString)
    },

    // `searchTerm` is read by exactly one page, pages/SearchResults.vue. Every
    // other query param (domain, adminSide, draftPreview, profileId, groupId,
    // id, control) is meaningful across a section change and must survive, so
    // section navigations carry the query minus this one key. Asana 1218127975719447.
    sectionQuery() {
      const query = { ...this.$route.query }
      delete query.searchTerm
      return query
    },
    changePage(page) {
      console.log('Inside changePage page', page)
      this.SET_CURRENT_LINK(page)
      if (page.controlsIndex) {
        this.SET_CURRENT_TAB(page.controlsIndex)
      }
      this.$router.push({
        path: `/${page.value}`,
        query: this.sectionQuery(),
      })
    },
    removeHTMLTags(html) {
      if (_.isEmpty(html)) return html

      // Remove specific tags but preserve spaces
      html = html.replace(/<\/?p>/g, ' ') // Replace <p> tags with spaces

      if (this.containsHTMLTags(html)) {
        let doc = new DOMParser().parseFromString(html, 'text/html')

        // Extract text while keeping spacing
        return doc.body.innerText || ''
      }
      return html
    },
    removeHtmlComments(text) {
      if (!text) return ''
      return (
        text
          ?.replace(/<!--[\s\S]*?-->/g, '') // remove HTML comments
          .replace(/&nbsp;/g, '') // remove non-breaking spaces
          .trim() || ''
      )
    },
    removeStartingBrTags(str) {
      return str.replace(/^(\s*<br\s*\/?>)+/i, '')
    },

    getWatermark() {
      return (
        this.GET_TRUST_CENTER_DB.find((i) => i.type === 'trustCenterSettings')
          ?.watermark ?? ''
      )
    },
    getTrustCenterLogoLink() {
      return (
        this.GET_TRUST_CENTER_DB.find((i) => i.type === 'trustCenterSettings')
          ?.trustCenterLogoLink ?? ''
      )
    },

    subStringText(str, maxLength, substring) {
      if (!str || str.length === 0) return ''
      return this.cleanHtml(
        str.length > maxLength ? `${str.substring(0, substring)}...` : str
      )
    },
    getIconByFileName(name) {
      let extension = name
        ? name.split('.')[name.split('.').length - 1].toLowerCase()
        : ''
      if (extension === '') {
        // Removed: red pdf-svgrepo-com.svg fallback — trust center docs have
        // no vendorDocName so they all landed here (design feedback, Asana
        // 1216389183156551). Same icon as the explicit pdf branch instead.
        return require('@/assets/pdf-v2.svg')
      } else if (extension === 'pdf') {
        return require('@/assets/pdf-v2.svg')
      } else if (
        ['csv', 'xls', 'xlsx', 'xlsm', 'xlsb', 'ods'].includes(extension)
      ) {
        return require('@/assets/xls.svg')
      } else if (['doc', 'docx'].includes(extension.toLowerCase()))
        return require('@/assets/word-v2.svg')
      else if (
        [
          'jpg',
          'jpeg',
          'svg',
          'gif',
          'tiff',
          'tif',
          'png',
          'webp',
          'bmp',
        ].includes(extension)
      )
        return require('@/assets/img-v2.svg')
      else return require('@/assets/file.svg')
    },
    async threeDotsEffect(elm, time) {
      await this.sleep(1000) // element not exist in the dom yet
      const elmToTarget = document.querySelector(elm)
      if (_.isEmpty(elmToTarget)) return // no element on dome
      elmToTarget.textContent = '' // clear the text content if exist

      const dotsInterval = setInterval(() => {
        if (!_.isEmpty(this.GET_ANSWERS_GENERATED)) {
          // check with john how to make getter dynemic
          clearInterval(dotsInterval)
        }
        elmToTarget.textContent += '.'
        if (elmToTarget.textContent.length === 4) elmToTarget.textContent = ''
      }, time)
    },
    tidToWebsite(tid) {
      let res = ''
      // Extract the domain and replace `[.]` with `.`
      const domainMatch = tid.match(/@([^@]+)/) // Match everything after '@'
      let domain = domainMatch ? domainMatch[1].replace(/\[\.\]/g, '.') : null

      if (domain) {
        res = `https://${domain}` // Prepend the protocol
      }
      return res
    },
  },

  computed: {
    ...mapGetters([
      'GET_CURRENT_USER',
      'GET_TRUST_CENTER_DB',
      'GET_TEAMID',
      'GET_HAS_TOP_NAVBAR_BG_IMAGE',
    ]),
    isEdit() {
      // should be true if the user is on the admin side (checking if the referrer is the appURL + query param adminSide)
      return this.$route.query.adminSide === 'true'
    },
    isLoggedNonAnonymously() {
      // Require a real signed-in user (uid), not just any truthy object:
      // an empty/stale {} user must never pass the request-access gate.
      return !!(
        this.GET_CURRENT_USER?.uid && !this.GET_CURRENT_USER.isAnonymous
      )
    },

    hasUser() {
      return !_.isEmpty(_.get(this.GET_CURRENT_USER, 'email'))
    },

    windowWidth() {
      return this.$store.getters.GET_WINDOW_WIDTH
    },

    showUploadQuestionnaireButton() {
      // Hide on screens smaller than 1280px regardless of settings
      if (this.windowWidth < 1280) {
        return false
      }
      // Show button only if at least one option is available
      return (
        (this.isSelfServiceAllowed || this.isSendToAdminAllowed) && this.hasUser
      )
    },
    getTidForDiffrentLogoDesign() {
      // The setting (vendict app: Trust Center > Settings > General > "Logo in
      // Navigation Bar", stored on the trustCenterSettings doc) takes
      // precedence once the admin has touched the toggle (persisted as an
      // explicit true/false). Read like isSearchBarDisplay (top-level field).
      const inlineNavbarLogo = this.trustCenterSettingsComputed?.inlineNavbarLogo
      if (inlineNavbarLogo !== undefined && inlineNavbarLogo !== null) {
        return Boolean(inlineNavbarLogo)
      }
      // Legacy hardcoded tenants: fallback until their settings doc carries
      // inlineNavbarLogo, so deploying this change alone is not a regression.
      const tids = ['.*@squareup[.]com', '.*@block[.]xyz', '.*@andalanglobal[.]app']
      return tids.includes(this.GET_TEAMID)
    },

    getBrending() {
      const res = this.GET_TRUST_CENTER_DB.find(
        (item) => item.type === 'branding'
      )
      console.log(
        'Inside getBrending this.GET_TRUST_CENTER_DB, res',
        this.GET_TRUST_CENTER_DB,
        res
      )
      return res ?? {}
    },
    trustCenterSettingsComputed() {
      return this.getTrustCenterByType('trustCenterSettings') ?? {}
    },
    trustCenterLogoLink() {
      let res = null
      let defoultLink =
        this.tidToWebsite(this.GET_TEAMID) !== ''
          ? this.tidToWebsite(this.GET_TEAMID)
          : '/'
      let linkFromSettings = this.getTrustCenterLogoLink()
      if (linkFromSettings) {
        res = linkFromSettings
      } else {
        res = defoultLink
      }
      return res
    },
    allowedAuthMethods() {
      return _.get(this.trustCenterSettingsComputed, 'allowedAuthMethods', [])
    },
    shouldHideSearchBar() {
      // Access isSearchBarDisplay directly from trust center database
      const trustCenterSettings = this.GET_TRUST_CENTER_DB.find(
        (item) => item.type === 'trustCenterSettings'
      )
      console.log(
        'Inside shouldHideSearchBar trustCenterSettings',
        trustCenterSettings?.isSearchBarDisplay
      )
      return trustCenterSettings?.isSearchBarDisplay === false
    },
    searchBarPosition() {
      // Returns 'above' (default) or 'below' to control where the search bar
      // is rendered relative to the main navigation bar.
      const trustCenterSettings = this.GET_TRUST_CENTER_DB.find(
        (item) => item.type === 'trustCenterSettings'
      )
      return trustCenterSettings?.searchBarPosition === 'below'
        ? 'below'
        : 'above'
    },
    searchBarLocation() {
      // Returns 'topNavbar' (default) or 'mainNavbar' to control which navbar
      // component renders the AI search bar. Set in the admin Trust Center
      // settings ("AI Search Bar Location").
      const trustCenterSettings = this.GET_TRUST_CENTER_DB.find(
        (item) => item.type === 'trustCenterSettings'
      )
      return trustCenterSettings?.searchBarLocation === 'mainNavbar'
        ? 'mainNavbar'
        : 'topNavbar'
    },
    isSearchBarInMainNavbar() {
      return this.searchBarLocation === 'mainNavbar'
    },
    isSearchBarBelowNavbar() {
      return this.searchBarPosition === 'below'
    },
    // True when the top-navbar has no background image AND the search bar is
    // rendered below the navbar. In that layout the top-navbar collapses to
    // host only the search input, so consumers can drop the extra top padding.
    isTopNavbarCompact() {
      return this.isSearchBarBelowNavbar && !this.GET_HAS_TOP_NAVBAR_BG_IMAGE
    },
  },
}
